import React, { Component } from 'react';
import PropTypes from 'prop-types';
import throttle from 'lodash.throttle';

import './style.css';

const propTypes = {
	// Image
	src: PropTypes.string.isRequired,
	alt: PropTypes.string,
	width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),


	// Zoom image
	zoomImgSrc: PropTypes.string,
	zoomFactor: PropTypes.number,


	// Magnifying glass
	mgWidth: PropTypes.number,
	mgHeight: PropTypes.number,
	mgShape: PropTypes.oneOf(['circle', 'square']),
	mgMouseOffsetX: PropTypes.number,
	mgMouseOffsetY: PropTypes.number,
	mgTouchOffsetX: PropTypes.number,
	mgTouchOffsetY: PropTypes.number
};


const defaultProps = {
	// Image
	alt: null,
	width: '100%',
	height: 'auto',


	// Zoom image
	zoomImgSrc: null,
	zoomFactor: 1.5,

	// Magnifying glass
	mgWidth: 150,
	mgHeight: 150,
	mgShape: 'square',
	mgMouseOffsetX: 0,
	mgMouseOffsetY: 0,
	mgTouchOffsetX: -50,
	mgTouchOffsetY: -50
};

export default class Magnifier extends Component {
	constructor(props) {
		super(props);

		if (!this.props.src) {
			throw Error('Missing src prop');
		}

		this.state = {
			showZoom: false,

			// Absolute image size
			absWidth: null,
			absHeight: null,

			// Magnifying glass offset
			mgOffsetX: 0,
			mgOffsetY: 0,

			// Mouse position relative to image
			relX: 0,
			relY: 0,

			videoTop: this.props.videoPosition.top,
			videoLeft: this.props.videoPosition.left,
			videoHeight: this.props.videoPosition.height,
			videoWidth: this.props.videoPosition.width,
			windowHeight: window.innerHeight
		};

		// Function bindings
		this.onMouseMove = throttle(this.onMouseMove.bind(this), 20, { trailing: false });
		this.onMouseOut = this.onMouseOut.bind(this);
		this.onTouchMove = throttle(this.onTouchMove.bind(this), 20, { trailing: false });
		this.onTouchEnd = this.onTouchEnd.bind(this);
	}

	componentDidMount() {
		// Add non-passive event listeners to 'nv' element (assigned in render function)
		this.element.addEventListener('mousemove', this.onMouseMove, { passive: false });
		this.element.addEventListener('mouseout', this.onMouseOut, { passive: false });
		this.element.addEventListener('touchstart', this.onTouchStart, { passive: false });
		this.element.addEventListener('touchmove', this.onTouchMove, { passive: false });
		this.element.addEventListener('touchend', this.onTouchEnd, { passive: false });
	}

	componentWillUnmount() {
		// Remove all event listeners
		this.element.removeEventListener('mousemove', this.onMouseMove);
		this.element.removeEventListener('mouseout', this.onMouseMove);
		this.element.removeEventListener('touchstart', this.onMouseMove);
		this.element.removeEventListener('touchmove', this.onMouseMove);
		this.element.removeEventListener('touchend', this.onMouseMove);
	}

	onMouseMove(e) {
		const imgBounds = e.target.getBoundingClientRect();
		imgBounds.width = imgBounds
		let videoContainer = document.getElementById('magnifier-id')
   		this.setState({
			showZoom: true,
			relX: (e.clientX - imgBounds.x) / e.target.clientWidth,
			relY: (e.clientY - imgBounds.y) / e.target.clientHeight,
			mgOffsetX: this.props.mgMouseOffsetX,
			mgOffsetY: this.props.mgMouseOffsetY,
			videoTop: videoContainer.offsetTop,
			videoLeft: videoContainer.offsetLeft,
			videoHeight: videoContainer.clientHeight,
			videoWidth: videoContainer.clientWidth
		});
	}

	onTouchStart(e) {
		e.preventDefault(); // Prevent mouse event from being fired
	}

	onTouchMove(e) {
		e.preventDefault(); // Disable scroll on touch
		const imgBounds = e.target.getBoundingClientRect();
		const relX = (e.targetTouches[0].clientX - imgBounds.left) / e.target.clientWidth;
		const relY = (e.targetTouches[0].clientY - imgBounds.top) / e.target.clientHeight;

		// Only show magnifying glass if touch is inside image
		if (relX >= 0 && relY >= 0 && relX <= 1 && relY <= 1) {
			this.setState({
				showZoom: true,
				relX,
				relY,
				mgOffsetX: this.props.mgTouchOffsetX,
				mgOffsetY: this.props.mgTouchOffsetY
			});
		} else {
			this.setState({
				showZoom: false
			});
		}
	}

	onMouseOut() {
		this.setState({
			showZoom: false
		})
	}

	onTouchEnd() {
		this.setState({
			showZoom: false
		});
	}

	render() {
		// Show/hide magnifying glass (opacity needed for transition)
		let mgClasses = 'magnifying-glass';
		if (this.state.showZoom) {
			mgClasses += ' visible';
		}
		if (this.props.mgShape === 'circle') {
			mgClasses += ' circle';
		}

		let windowHeight = window.innerHeight
		let windowWidth = window.innerWidth
		let videoTop = this.state.videoTop
		let videoLeft = this.state.videoLeft
		let videoWidth = this.state.videoWidth
		let magnifyingGlassHeight = this.props.mgHeight
		let magnifyingGlassWidth = this.props.mgWidth
		let videoHeight = this.state.videoHeight
		let maxTop = windowHeight - videoTop - magnifyingGlassHeight
		let maxLeft = windowWidth - videoLeft - magnifyingGlassWidth
		let topWouldBe = (this.state.relY * videoHeight) - (this.props.mgHeight / 2) + this.state.mgOffsetY
		let leftWouldBe = (this.state.relX * videoWidth) - (this.props.mgWidth / 2) + this.state.mgOffsetX
		let top = Math.min(maxTop, topWouldBe)
		let left = Math.min(maxLeft, leftWouldBe)
		
		return (
			<div
				className="magnifier"
				id='magnifier-id'
				style={{
					width: this.props.width,
					height: this.props.height
				}}
			>
				<img
					className="magnifier-image"
					src={this.props.src}
					alt={this.props.alt}
					width="100%"
					height="100%"
					onLoad={(e) => {
						this.setState({
							absWidth: e.target.width,
							absHeight: e.target.height
						});
					}}
					ref={(e) => {
						this.element = e;
					}}
				/>
				<div
					className={mgClasses}
					style={{
						width: this.props.mgWidth,
						height: this.props.mgHeight,
						left: `${left}px`,
						top: `${top}px`,
						backgroundImage: `url(${this.props.zoomImgSrc || this.props.src})`,
						backgroundPosition: `${this.state.relX * 100}% ${this.state.relY * 100}%`}}
				/>
			</div>
		);
	}
}

Magnifier.propTypes = propTypes;
Magnifier.defaultProps = defaultProps;



// WEBPACK FOOTER //
// ./src/components/video/magnifier.js